import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface CardProps {
  icon: string;
  year: string;
  title: string;
  desc: string;
  href?: string;
  details: string[];
}

const Card: React.FC<CardProps> = (props) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  DOMPurify.addHook("afterSanitizeAttributes", function (node: Element) {
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  const extractUrl = (htmlString: string): string | null => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const anchor = doc.querySelector("a");
    return anchor ? anchor.getAttribute("href") : null;
  };

  const extractText = (htmlString: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    return doc.body.textContent || "";
  };

  const companyUrl = extractUrl(props.title);
  const companyName = extractText(props.title);

  return (
    <div className="timeline__item">
      <i className={props.icon}></i>
      <span className="timeline__date">{props.year}</span>

      <div className="timeline__title-container">
        <h3 className="timeline__title">{companyName}</h3>
        {companyUrl && (
          <a
            className="timeline__company-link"
            target="_blank"
            rel="noopener noreferrer"
            href={companyUrl}
            title={`Visit ${companyName} website`}
          >
            <i className="icon-link"></i>
          </a>
        )}
      </div>

      <div className="timeline__content">
        <span className="timeline__text">{props.desc}</span>
        {props.href && (
          <a
            className="timeline__link"
            target="_blank"
            rel="noopener noreferrer"
            href={props.href}
          >
            <i className="icon-link"></i>
          </a>
        )}
      </div>

      {props.details && props.details.length > 0 && (
        <div className="timeline__details-container">
          <div className={`timeline__details ${showDetails ? "expanded" : ""}`}>
            <ul className="timeline__details-list">
              {props.details.map((detail, index) => (
                <li key={index} className="timeline__detail-item">
                  {detail}
                </li>
              ))}
            </ul>
          </div>
          {isMobile && (
            <button
              className="timeline__details-toggle"
              onClick={() => setShowDetails(!showDetails)}
              aria-expanded={showDetails}
            >
              {showDetails ? "Show Less" : "Show More"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Card;
